import React, { useCallback, useEffect, useState } from "react";
import { FormHeader } from "@components/Header";
import { useNavigate } from "react-router-dom";
import {
  FormBody,
  FormContentContainer,
  FullScreenLoader,
  Modal,
} from "@components";
import { Box, CircularProgress } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  useBankTransferAttempts,
  useCreateBankTransferChallenge,
  useVerifyBankTransferStatus,
} from "@hooks";
import { useSnackbar } from "notistack";
import { BankTransactionStatus } from "@interfaces";
import { ENROLMENT_STEP_STATUS } from "@contexts/EnrolmentStatusContext";
import { tracker } from "@utils/openreplay";
import { getEnrolmentErrorDisplay } from "../hooks/onQuestionChallengeError";
import { Routes } from "@utils";
import {
  ContentBox,
  LogoWrapper,
  LogoImage,
  TitleTypography,
  BodyTypography,
  SmallBodyTypography,
  SemiBold,
  ButtonsRow,
  StyledSecondaryActionButton,
  StyledBottomActionButton,
} from "./StartBankTransferScreen.styles";
import { useEnrolmentStatusContext } from "@contexts";
import { BankTransferFailureScreen } from "./BankTransferFailureScreen";

const SOURCE = "StartBankTransferScreen";

export const StartBankTransferScreen: React.FC = () => {
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { getEnrolmentStatus, currentStepStatus } =
    useEnrolmentStatusContext();
  const { hasExhaustedAttempts, resetAttempts } = useBankTransferAttempts();
  const { mutateAsync: createChallengeRequest } =
    useCreateBankTransferChallenge({
      onError: (error) => {
        setIsLoading(false);
        tracker?.event("BankTransferChallengeRequestedError", {
          source: SOURCE,
          error: error,
          errorMessage: error.message,
        });
        enqueueSnackbar(getEnrolmentErrorDisplay(error).name, {
          variant: "error",
        });
        navigate(Routes.SOLICITUDES);
      },
    });

  const isTransferInFlight =
    currentStepStatus === ENROLMENT_STEP_STATUS.STARTED;
  const { data: transferStatusData, error: errorGettingTransactionStatus } =
    useVerifyBankTransferStatus({ enabled: isTransferInFlight });

  const bankTransferStatus = transferStatusData?.bankTransferStatus;
  const isApproved = bankTransferStatus === BankTransactionStatus.APPROVED;
  const isStillProcessing =
    isTransferInFlight &&
    !errorGettingTransactionStatus &&
    bankTransferStatus !== BankTransactionStatus.REJECTED;

  useEffect(() => {
    if (!isApproved) return;
    resetAttempts();
    getEnrolmentStatus();
  }, [isApproved, resetAttempts, getEnrolmentStatus]);

  const handleRealizarTransferencia = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmAccept = useCallback(async () => {
    tracker?.event("BankTransferChallengeRequested", { source: SOURCE });
    setShowConfirmModal(false);
    setIsLoading(true);
    try {
      await createChallengeRequest();
    } catch {
      // Error already handled by onError callback
    }
  }, [createChallengeRequest]);

  if (isStillProcessing) {
    return (
      <>
        <FormHeader useMekiDocIcon />
        <FormContentContainer>
          <FormBody justifyContent="center">
            <FullScreenLoader title="Verificando estado de la transferencia" />
            <StyledSecondaryActionButton
              onClick={() => navigate(Routes.SOLICITUDES)}
            >
              Volver al inicio
            </StyledSecondaryActionButton>
          </FormBody>
        </FormContentContainer>
      </>
    );
  }

  if (hasExhaustedAttempts) {
    return (
      <BankTransferFailureScreen
        source={SOURCE}
        onRetry={handleRealizarTransferencia}
        isRetrying={isLoading}
      />
    );
  }

  return (
    <>
      <FormHeader useMekiDocIcon />
      <FormContentContainer>
        <FormBody justifyContent="center">
          <ContentBox>
            <LogoWrapper>
              <LogoImage src="/assets/logos/khipu.png" alt="Khipu" />
            </LogoWrapper>
            <TitleTypography>
              Verificación de identidad con transferencia
            </TitleTypography>
            <BodyTypography>
              Completa la verificación con una{" "}
              <SemiBold>transferencia bancaria a través de Khipu</SemiBold>{" "}
              (valor entre $100 y $2.000)
            </BodyTypography>
            <BodyTypography>
              <SemiBold>Será reembolsado automáticamente</SemiBold> en las
              próximas 24 hrs.
            </BodyTypography>
            <SmallBodyTypography>
              Khipu está regulado por la CMF ley 21.521.
            </SmallBodyTypography>
          </ContentBox>
          <ButtonsRow>
            <StyledSecondaryActionButton
              onClick={() => navigate(Routes.SOLICITUDES)}
              disabled={isLoading}
            >
              Volver al inicio
            </StyledSecondaryActionButton>
            <StyledBottomActionButton
              onClick={handleRealizarTransferencia}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Realizar transferencia"
              )}
            </StyledBottomActionButton>
          </ButtonsRow>
        </FormBody>
      </FormContentContainer>

      <Modal
        title={
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1.5}
          >
            <ErrorOutlineIcon
              sx={{ fontSize: 40, width: 40, height: 40, color: "#ED6C02" }}
            />
            <TitleTypography style={{ textAlign: "center" }}>
              No cierres esta pestaña ni la de Khipu
            </TitleTypography>
          </Box>
        }
        content="Espera la redirección automática de vuelta a MekiDoc al finalizar, para completar la verificación."
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        buttonProps={{
          buttonText: "Aceptar",
          onClick: handleConfirmAccept,
          loading: isLoading,
          disabled: isLoading,
        }}
      />
    </>
  );
};
